import type { IContainersObject } from '../interfaces/config'

/**
 * A `CartCheckoutPanel` instance authored with `variant: 'summary'` into a
 * container section that has no sibling `SummaryList` to inherit chrome from.
 *
 * The library's own guard at `CartCheckoutPanel.tsx` keys on content, not
 * ancestor, so the panel renders anyway — chromeless on the page background,
 * full-bleed, below whatever CTA sits in the same section. See COM-901 for
 * the `mondelez-mx-b2b-ng` incident the shape here is modelled on.
 */
export interface CartCheckoutSummaryPlacementIssue {
  containerKey: string
  section: 'header' | 'body' | 'footer'
  uid: string
}

const SECTIONS = ['header', 'body', 'footer'] as const

/**
 * Flags `CartCheckoutPanel` summary instances authored into container sections
 * with no sibling `SummaryList`. Nothing in this returns a decision about the
 * container's `type` — a Summary-type container whose author left the list out
 * is as broken as a Cart whose author added the summary variant in error, and
 * the signal is the same either way: a chromeless row with no host.
 *
 * Reads the processed config rather than the authored one, matching
 * `isHidePricesMisconfigured` and `isPackageSortMisconfigured`. `variant` is
 * a literal string prop the author sets, and `processConfig` does not rewrite
 * it, so the processed value is the authored one. A `{{token}}` that failed
 * to resolve becomes `undefined`, which falls out of the equality check
 * silently — the panel's own data guard then renders nothing, which is the
 * only state a config author cannot distinguish from an authoring typo and
 * also the one state that is not a bug.
 *
 * Cross-section placement (summary in `body`, SummaryList in `footer`) is
 * flagged: the two must sit in the same `IContainerSections.components`
 * array to visually compose, since each section's components render into a
 * different wrapper and the panel inherits chrome from its siblings' box,
 * not from a cousin's.
 *
 * Returns an empty array if nothing is amiss. Caller reports; the predicate
 * does not touch Datadog itself. See the call site in
 * `redux/slices/config/config.ts`'s `fetchConfig` thunk.
 */
export const findMisplacedCartCheckoutSummaries = (
  containers: IContainersObject | undefined,
): CartCheckoutSummaryPlacementIssue[] => {
  if (!containers) {
    return []
  }

  const issues: CartCheckoutSummaryPlacementIssue[] = []

  Object.entries(containers).forEach(([containerKey, container]) => {
    SECTIONS.forEach((section) => {
      const components = container?.[section]?.components
      if (!Array.isArray(components) || components.length === 0) {
        return
      }

      const hasSummaryList = components.some(
        (component) => component?.importRoute === 'SummaryList',
      )

      components.forEach((component) => {
        if (
          component?.importRoute === 'CartCheckoutPanel' &&
          component?.props?.variant === 'summary' &&
          !hasSummaryList
        ) {
          issues.push({ containerKey, section, uid: component.uid })
        }
      })
    })
  })

  return issues
}
